import React, { useState, useEffect } from 'react';
import { Bell, ShieldCheck, Zap, X, CheckCircle2, Volume2, AlertCircle } from 'lucide-react';
import { getAppNotificationPermission, requestAppNotificationPermission } from '../lib/notifications';

interface NotificationPromptModalProps {
  onSuccess?: () => void;
}

export function NotificationPromptModal({ onSuccess }: NotificationPromptModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [justActivated, setJustActivated] = useState(false);

  useEffect(() => {
    // Check permission on mount
    const checkPerm = async () => {
      // Check if user dismissed it in this browser session
      const dismissed = sessionStorage.getItem('pulse_notif_prompt_dismissed');
      if (dismissed) return;

      const perm = await getAppNotificationPermission();
      if (perm === 'prompt') {
        // Delay slightly for smooth page entrance
        const timer = setTimeout(() => {
          setIsOpen(true);
        }, 1200);
        return () => clearTimeout(timer);
      }
    };

    checkPerm();
  }, []);

  const handleEnable = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await requestAppNotificationPermission();
      if (res.success) {
        setJustActivated(true);
        setTimeout(() => {
          setIsOpen(false);
          if (onSuccess) onSuccess();
        }, 2200);
      } else {
        setError(res.error || 'Permission was not granted.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to request notification permission');
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    sessionStorage.setItem('pulse_notif_prompt_dismissed', 'true');
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-md animate-fade-in">
      <div className="relative w-full max-w-md rounded-3xl bg-[#0E0E0E] border border-pulse-blue/40 shadow-2xl shadow-pulse-blue/20 p-6 sm:p-7 overflow-hidden text-center">
        {/* Glow backdrop */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-32 bg-pulse-blue/15 blur-3xl pointer-events-none" />

        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 p-1.5 rounded-full text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors"
          title="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>

        {justActivated ? (
          <div className="py-6 space-y-4 animate-scale-in">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-emerald-400 shadow-lg shadow-emerald-500/20">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-bold text-white tracking-tight">Notifications Activated!</h3>
            <p className="text-xs text-zinc-300 leading-relaxed max-w-xs mx-auto">
              Harmonic bell chime verified. You will receive instant inactivity warnings and workstation status updates.
            </p>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Header Icon */}
            <div className="relative w-16 h-16 mx-auto">
              <div className="absolute inset-0 rounded-2xl bg-pulse-blue/20 animate-ping opacity-75" />
              <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-pulse-blue to-indigo-600 flex items-center justify-center text-white shadow-xl shadow-pulse-blue/30 border border-pulse-blue/50">
                <Bell className="w-8 h-8" />
              </div>
            </div>

            {/* Title & Description */}
            <div className="space-y-2">
              <h3 className="text-lg sm:text-xl font-bold text-white tracking-tight">
                Allow Real-Time Alert Notifications
              </h3>
              <p className="text-xs text-zinc-400 leading-relaxed">
                Stay informed the exact second an employee workstation crosses an inactivity threshold or starts a break.
              </p>
            </div>

            {/* Feature Bullets */}
            <div className="p-3.5 rounded-2xl bg-zinc-950/80 border border-zinc-800/80 text-left space-y-2.5">
              <div className="flex items-center gap-2.5 text-xs text-zinc-300">
                <Zap className="w-4 h-4 text-pulse-blue shrink-0" />
                <span>Instant alerts directly on lock screen & desktop</span>
              </div>
              <div className="flex items-center gap-2.5 text-xs text-zinc-300">
                <Volume2 className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>Plays audible chime when stillness limits are crossed</span>
              </div>
              <div className="flex items-center gap-2.5 text-xs text-zinc-300">
                <ShieldCheck className="w-4 h-4 text-amber-400 shrink-0" />
                <span>100% privacy-compliant (Zero keylogging or screenshots)</span>
              </div>
            </div>

            {error && (
              <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-400 text-xs flex items-center gap-2 text-left">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Actions */}
            <div className="space-y-2 pt-1">
              <button
                onClick={handleEnable}
                disabled={loading}
                className="w-full py-3 px-4 rounded-xl text-xs sm:text-sm font-semibold bg-pulse-blue hover:bg-pulse-blueHover text-white shadow-lg shadow-pulse-blue/30 transition-all flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.98]"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Requesting Permission...</span>
                  </>
                ) : (
                  <>
                    <Bell className="w-4 h-4" />
                    <span>Enable Notifications Now</span>
                  </>
                )}
              </button>

              <button
                onClick={handleDismiss}
                className="w-full py-2.5 px-4 text-xs font-medium text-zinc-400 hover:text-white transition-colors"
              >
                Remind Me Later
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
